import { useStoreState } from '@ariakit/react'
import { FC, useMemo } from 'react'
import styled from 'styled-components'

import { Pencil, TypeStyle } from '@syconium/little-miss-figgy'

import { useTranslation } from '../../../../../../app/_providers/TranslationProvider.client'
import { useEmbroidery } from '../../../../../../containers/EmbroideryContainer/EmbroideryProvider'
import { LogoFile } from '../../../../../../containers/EmbroideryContainer/types'
import { ErrorMessage, Label } from '../../../../Modal/primitives/shared-form-styles'
import { LogoFormContainer } from '../LogoFormContainer'

import { AddNewLogoButton } from './AddNewLogoButton'
import { LogoColorDisplay } from './LogoColorDisplay'
import * as LogoPreviewItem from './LogoPreviewItem'

export const LogoSelector: FC = () => {
  const {
    embroidery: {
      modal: {
        forms: { dtcLogoEmbroideryForm: fixtures, logoOption },
      },
    },
  } = useTranslation()

  const { submittedEmbroiderySelections } = useEmbroidery()
  const { addLogoForm, logoFiles, includesDigitization, transientLogo, displayLogoForm } =
    LogoFormContainer.useContainer()

  const logoFileId = useStoreState(addLogoForm, s => s.values.logoFileId)
  const logoFile = useStoreState(addLogoForm, s => s.values.logoFile)
  const logoFileError = useStoreState(addLogoForm, () => addLogoForm.getError('logoFileId'))

  // TODO: need to confirm but the API may only return 'PENDING' state only even though there are multiple states
  const logoStates = {
    pending: [
      'PENDING',
      'SENT_TO_DIGITIZER',
      'DIGITIZATION_UNDER_REVIEW',
      'SENT_FOR_CUSTOMER_APPROVAL',
      'CUSTOMER_FEEDBACK',
    ],
    approved: ['APPROVED'],
    canceled: ['CANCELED'],
  }

  const handleLogoSelection = (selectedLogo: LogoFile) => {
    addLogoForm.setValue('logoFile', selectedLogo)
    addLogoForm.setValue('logoFileId', selectedLogo.id)
  }

  const logoColorOptionTitle = useMemo(() => {
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    const digitizationSetting = logoFile?.digitizationSetting
    if (!digitizationSetting) return undefined
    return digitizationSetting === 'BLACK_WHITE'
      ? // TODO: Fix this auto-generated suppression.
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        ` (${fixtures.logoStrings.LOGO_BLACK_WHITE?.title})`
      : // TODO: Fix this auto-generated suppression.
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      digitizationSetting === 'COLOR'
      ? // TODO: Fix this auto-generated suppression.
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        ` (${fixtures.logoStrings.LOGO_COLOR?.title})`
      : ''
  }, [fixtures.logoStrings, logoFile?.digitizationSetting])

  const canAddNewLogo = useMemo(() => {
    // If a transient logo exists in the cart, don't allow new logo requests
    if (transientLogo) return false
    // If there is an active logo setup, don't allow new logo requests
    if (includesDigitization) return false
    return true
  }, [transientLogo, includesDigitization])

  return (
    <>
      <StyledLabel id='logo-placement-group'>
        <div>
          {logoOption.logoLabel}
          <SelectedLogoLabel>
            {logoFile?.name}
            {logoColorOptionTitle}
          </SelectedLogoLabel>
        </div>
        <LogoColorDisplay />
      </StyledLabel>
      <LogoPreviewItem.GridContainer>
        {logoFiles.length > 0 &&
          logoFiles.map(logo => {
            // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
            const isEditable = submittedEmbroiderySelections.LOGO_SETUP?.logoFile?.id === logo.id
            const disabled = logoStates.pending.includes(logo.status) && !isEditable
            const isApprovedLogo = logo.status === 'APPROVED'
            return (
              <div role='group' aria-labelledby='logo-placement-group' key={logo.id}>
                <LogoPreviewItem.Container>
                  <LogoPreviewItem.Label>
                    <LogoPreviewItem.Input
                      type='radio'
                      name='logoFileId'
                      disabled={disabled}
                      checked={logoFileId === logo.id}
                      value={logo.id}
                      onChange={event => {
                        if (event.target.checked) {
                          handleLogoSelection(logo)
                        }
                      }}
                    />

                    <LogoPreviewItem.Image
                      src={logo.preview}
                      aspectRatios={{
                        sm: null,
                        md: null,
                      }}
                      loading='lazy'
                      alt={logo.name}
                      widths={{ unit: 'vw', sm: 100, md: 50 }}
                      transparentBackground={true}
                    />
                  </LogoPreviewItem.Label>
                  {!isApprovedLogo && isEditable ? (
                    <LogoPreviewItem.Status>{fixtures.digitizationRequest}</LogoPreviewItem.Status>
                  ) : logoStates.pending.includes(logo.status) ? (
                    <LogoPreviewItem.Status>{fixtures.digitizationPending}</LogoPreviewItem.Status>
                  ) : null}
                  {!isApprovedLogo && includesDigitization && isEditable && (
                    <LogoPreviewItem.EditIcon
                      type='button'
                      onClick={() => displayLogoForm('LOGO_SETUP')}
                    >
                      <PencilIcon />
                    </LogoPreviewItem.EditIcon>
                  )}
                </LogoPreviewItem.Container>
              </div>
            )
          })}
        {canAddNewLogo ? <AddNewLogoButton /> : null}
      </LogoPreviewItem.GridContainer>
      {logoFileError && (
        <ErrorMessage inset role='alert'>
          {logoFileError}
        </ErrorMessage>
      )}
    </>
  )
}

const StyledLabel = styled(Label)`
  margin-top: ${o => o.theme.spacing(6)};
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
`

const SelectedLogoLabel = styled.span`
  ${TypeStyle.css.headlineMicro}
  padding-left: ${({ theme }) => theme.spacing(1)};
  text-transform: none;
  color: ${o => o.theme.color.deprecated.obsidianLight};
  font-weight: ${o => o.theme.typography.font.weight[400]};
`

const PencilIcon = styled(Pencil)`
  position: relative;
  top: -${o => o.theme.spacing(0.5)};
  right: ${o => o.theme.spacing(0.5)};
`
